import { useEffect, useState } from "react";
import { NAVY } from "../styles/palette";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { useIsMobile } from "../lib/useIsMobile";

export function TechJobList({ tech, onRefresh, showToast }) {
  const isMobile = useIsMobile();
  const [jobs,    setJobs]    = useState([]);
  const [loading, setLoading] = useState(true);
  // Phone: visit notes are typed into an inline textarea under the row (window.prompt is
  // suppressed in a Home-Screen web app on iOS and silently returned null — the note was
  // dropped and the job completed with none). {jobId, text} while a note is being typed.
  const [noteFor, setNoteFor] = useState(null);
  const [noteText, setNoteText] = useState("");
  const [completing, setCompleting] = useState(false);
  const today = new Date().toISOString().split("T")[0];

  async function loadJobs() {
    setLoading(true);
    const rows = await fetch(`${SUPABASE_URL}/rest/v1/jobs?technician_id=eq.${tech.id}&scheduled_date=eq.${today}&order=client_name.asc`,{
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`}
    }).then(r=>r.json());
    if (Array.isArray(rows)) setJobs(rows);
    setLoading(false);
  }

  useEffect(()=>{ loadJobs(); },[tech.id]); // eslint-disable-line react-hooks/exhaustive-deps

  async function startJob(job) {
    await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${job.id}`,{
      method:"PATCH",
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
      body:JSON.stringify({status:"started",started_at:new Date().toISOString()})
    });
    showToast(`Started job at ${job.client_name}`);
    loadJobs();
  }

  async function completeJob(job, note) {
    setCompleting(true);
    const res = await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${job.id}`,{
      method:"PATCH",
      headers:{apikey:SUPABASE_ANON_KEY,Authorization:`Bearer ${getAuthToken()}`,"Content-Type":"application/json",Prefer:"return=minimal"},
      body:JSON.stringify({status:"completed",completed_at:new Date().toISOString(),visit_notes:(note||"").trim()||null})
    }).catch(()=>null);
    setCompleting(false);
    if (!res || !res.ok) { showToast(`Couldn't complete job at ${job.client_name}`, "error"); return; }
    setNoteFor(null); setNoteText("");
    showToast(`Completed job at ${job.client_name}`);
    loadJobs();
  }

  // Desktop keeps the native prompt; the phone reveals the inline notes field instead.
  function onCompleteTap(job) {
    if (!isMobile) { completeJob(job, prompt(`Visit notes for ${job.client_name} (optional):`) || ""); return; }
    setNoteFor(job.id); setNoteText("");
  }

  if (!jobs.length && !loading) return null;

  const isAuto = tech.job_completion_mode === "auto";

  return (
    <div className="card" style={{marginBottom:16}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div className="card-title" style={{marginBottom:0}}>📋 Today's Jobs — {today}</div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {isAuto&&<span style={{fontSize:11,background:"#dcfce7",color:"#065F46",padding:"2px 8px",borderRadius:4,fontWeight:600}}>📍 GPS Auto-Mode Active</span>}
          <button className="btn btn-ghost btn-sm" style={isMobile?{minWidth:40}:undefined} aria-label="Refresh" onClick={loadJobs}>🔄</button>
        </div>
      </div>
      {loading&&<div style={{padding:12,textAlign:"center",color:"#8993a4",fontSize:12}}>Loading...</div>}
      {jobs.map(job=>{
        const statusColor = job.status==="completed"?"#065F46":job.status==="started"?"#B45309":"#8993a4";
        const statusBg    = job.status==="completed"?"#dcfce7":job.status==="started"?"#fffae6":"#f3f4f6";
        const statusLabel = job.status==="completed"?"✓ Completed":job.status==="started"?"● In Progress":"○ Scheduled";
        // Phone: finger-sized actions; the status/actions column becomes a wrapping row
        // under the name instead of squeezing the address to a few words per line.
        const actBtn = isMobile ? {fontSize:13,padding:"8px 14px",minHeight:40} : null;
        const noting = noteFor === job.id;
        return (
          <div key={job.id} style={{padding:"10px 12px",borderRadius:8,border:"1px solid #e3e8ef",marginBottom:8,background:job.status==="completed"?"#f9fffe":job.status==="started"?"#fffdf0":"white"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:8}}>
              <div style={{flex:"1 1 180px",minWidth:0}}>
                <div style={{fontWeight:700,fontSize:14,color:NAVY}}>{job.client_name}</div>
                <div style={{fontSize:12,color:"#8993a4"}}>{job.client_address}</div>
                {job.visit_notes&&<div style={{fontSize:11,color:"#42526e",marginTop:4,background:"#f5f6f8",padding:"3px 7px",borderRadius:4}}>📝 {job.visit_notes}</div>}
                {job.started_at&&<div style={{fontSize:11,color:"#8993a4",marginTop:2}}>Started: {new Date(job.started_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>}
                {job.completed_at&&<div style={{fontSize:11,color:"#8993a4"}}>Completed: {new Date(job.completed_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</div>}
              </div>
              <div style={isMobile
                ? {display:"flex",flexDirection:"row",flexWrap:"wrap",alignItems:"center",gap:8,flexBasis:"100%"}
                : {display:"flex",flexDirection:"column",alignItems:"flex-end",gap:6}}>
                <span style={{fontSize:11,fontWeight:600,padding:"2px 8px",borderRadius:4,background:statusBg,color:statusColor}}>{statusLabel}</span>
                {!isAuto&&job.status==="scheduled"&&(
                  <button className="btn btn-gold btn-sm" style={actBtn||{fontSize:11}} onClick={()=>startJob(job)}>▶ Start</button>
                )}
                {!isAuto&&job.status==="started"&&!noting&&(
                  <button className="btn btn-primary btn-sm" style={actBtn||{fontSize:11}} onClick={()=>onCompleteTap(job)}>✓ Complete</button>
                )}
                {isAuto&&job.status!=="completed"&&(
                  <div style={{fontSize:10,color:"#8993a4",textAlign:isMobile?"left":"right"}}>Auto-tracking{isMobile?" ":<br/>}GPS location</div>
                )}
                {/* Fallback manual buttons even in auto mode */}
                {isAuto&&job.status==="scheduled"&&(
                  <button className="btn btn-ghost btn-sm" style={actBtn||{fontSize:10}} onClick={()=>startJob(job)}>Manual Start</button>
                )}
                {isAuto&&job.status==="started"&&!noting&&(
                  <button className="btn btn-ghost btn-sm" style={actBtn||{fontSize:10}} onClick={()=>onCompleteTap(job)}>Manual Complete</button>
                )}
              </div>
            </div>
            {noting && (
              <div style={{marginTop:10,paddingTop:10,borderTop:"1px solid #e3e8ef"}}>
                <label style={{display:"block",fontSize:12,fontWeight:600,color:NAVY,marginBottom:6}}>Visit notes <span style={{fontWeight:400,color:"#8993a4"}}>(optional)</span></label>
                <textarea value={noteText} onChange={e=>setNoteText(e.target.value)} rows={3} autoFocus
                  placeholder="Gate was open, dog inside, etc."
                  style={{width:"100%",boxSizing:"border-box",padding:"10px 12px",borderRadius:8,border:"1px solid #dfe1e6",fontFamily:"inherit",resize:"vertical"}}/>
                <div style={{display:"flex",gap:8,marginTop:8}}>
                  <button className="btn btn-primary" style={{flex:1,minHeight:44}} disabled={completing} onClick={()=>completeJob(job, noteText)}>{completing?"Saving…":"✓ Complete job"}</button>
                  <button className="btn btn-ghost" style={{minHeight:44}} disabled={completing} onClick={()=>{setNoteFor(null); setNoteText("");}}>Cancel</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
